import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type PublicationUnit,
  type PublicationUnitDetail,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PAGE = 40;

function platformOf(u: PublicationUnit) {
  const ig = Boolean(u.ig_id);
  const fb = Boolean(u.fb_id);
  if (u.matched && ig && fb) return "IG+FB";
  if (ig && fb) return "IG+FB";
  if (ig) return "IG";
  if (fb) return "FB";
  return "–";
}

function fmtDate(iso?: string | null) {
  if (!iso) return "–";
  return iso.slice(0, 10);
}

function fmtNum(n?: number | null) {
  if (n == null) return "–";
  return String(Math.round(n));
}

function Thumb({ url, format }: { url?: string | null; format?: string | null }) {
  if (url) {
    return <img src={url} alt="" className="h-14 w-10 shrink-0 rounded object-cover bg-stone-100" loading="lazy" />;
  }
  return (
    <div className="flex h-14 w-10 shrink-0 items-center justify-center rounded bg-stone-100 text-xs font-semibold text-stone-500">
      {(format || "R").slice(0, 1).toUpperCase()}
    </div>
  );
}

function Chip({ children, tone = "stone" }: { children: React.ReactNode; tone?: string }) {
  const tones: Record<string, string> = {
    stone: "bg-stone-100 text-stone-700",
    emerald: "bg-emerald-100 text-emerald-900",
    amber: "bg-amber-100 text-amber-900",
    sky: "bg-sky-100 text-sky-900",
    rose: "bg-rose-100 text-rose-900",
  };
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", tones[tone] || tones.stone)}>
      {children}
    </span>
  );
}

function statusTone(status?: string | null) {
  const s = (status || "").toLowerCase();
  if (s === "ready") return "emerald";
  if (s === "no_audio" || s === "no_speech") return "amber";
  if (s === "media_unavailable") return "rose";
  return "stone";
}

/** Caption shown as "transcription" when ASR never ran or returned silence. */
function isCaptionFallback(source?: string | null, status?: string | null) {
  const src = (source || "").toLowerCase();
  const st = (status || "").toLowerCase();
  if (src.includes("asr_")) return false;
  if (st === "no_speech" || src.includes("no_speech")) return true;
  if (src === "caption" || src.startsWith("caption+")) return true;
  return false;
}

export default function Rolki() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [order, setOrder] = useState(params.get("order") || "published_at");
  const [platform, setPlatform] = useState(params.get("platform") || "");
  const [matched, setMatched] = useState(params.get("matched") || "");
  const [contentStatus, setContentStatus] = useState(params.get("status") || "");
  const [offset, setOffset] = useState(Number(params.get("offset") || 0) || 0);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    if (order) next.set("order", order);
    if (platform) next.set("platform", platform);
    else next.delete("platform");
    if (matched) next.set("matched", matched);
    else next.delete("matched");
    if (contentStatus) next.set("status", contentStatus);
    else next.delete("status");
    if (offset) next.set("offset", String(offset));
    else next.delete("offset");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, order, platform, matched, contentStatus, offset]);

  const listQ = useQuery({
    queryKey: ["rolki-units", q, order, platform, matched, contentStatus, offset],
    queryFn: () =>
      api.publicationsUnits({
        format: "reel",
        q: q || undefined,
        order,
        platform: platform || undefined,
        matched: matched === "true" ? true : matched === "false" ? false : undefined,
        content_status: contentStatus || undefined,
        limit: PAGE,
        offset,
      }),
  });

  const detailQ = useQuery({
    queryKey: ["rolki-unit", selectedId],
    queryFn: () => api.publicationsUnit(selectedId),
    enabled: Boolean(selectedId),
  });

  const commentsQ = useQuery({
    queryKey: ["rolki-comments", selectedId],
    queryFn: () => api.publicationsUnitComments(selectedId, 80),
    enabled: Boolean(selectedId),
  });

  const freshQ = useQuery({
    queryKey: ["rolki-freshness"],
    queryFn: () => api.publicationsFreshness(),
    refetchInterval: 60_000,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["rolki-units"] });
    void qc.invalidateQueries({ queryKey: ["rolki-freshness"] });
    void qc.invalidateQueries({ queryKey: ["rolki-unit"] });
  };

  const syncM = useMutation({
    mutationFn: () => api.publicationsSync({ days: 14, ingest: true, content: true, asr: false }),
    onSuccess: invalidate,
  });
  const rematchM = useMutation({
    mutationFn: () => api.publicationsRematchContent({ threshold: 0.9 }),
    onSuccess: invalidate,
  });
  const whisperM = useMutation({
    mutationFn: () => api.publicationsWhisperBackfill({ limit: 40, since: "2026-08-20" }),
    onSuccess: invalidate,
  });

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const detail: PublicationUnitDetail | undefined = detailQ.data?.unit;
  const stats = freshQ.data?.reel_stats;
  const bgJob =
    syncM.isPending
      ? "Sync z dumpów w toku (bez Whisper)…"
      : whisperM.isPending
        ? "Whisper backfill w toku (może potrwać kilka minut)…"
        : rematchM.isPending
          ? "Łączenie IG↔FB po ASR…"
          : null;

  const fmtWhen = (iso?: string | null) =>
    iso ? iso.slice(0, 19).replace("T", " ") : "–";

  const setStatusFilter = (status: string) => {
    setOffset(0);
    setContentStatus((prev) => (prev === status ? "" : status));
  };

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("id", id);
    setParams(next);
  };

  return (
    <div>
      <PageHeader
        title="Rolki"
        subtitle="Przeglądarka organicznych rolek IG/FB: statystyki, opis, transkrypcja, komentarze, odtwarzanie"
      />

      {bgJob ? (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          W tle: {bgJob}
        </div>
      ) : null}

      <Card className="mb-3">
        <CardContent className="space-y-3 pt-4">
          <div className="flex flex-wrap items-start gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <div>
              <div className="font-medium text-stone-800">Dump Graph (Celery)</div>
              <div>
                {fmtWhen(freshQ.data?.dumps?.last_run_at)} · {freshQ.data?.dumps?.status || "?"}
                {freshQ.data?.dumps?.trigger ? ` · ${freshQ.data.dumps.trigger}` : ""}
              </div>
            </div>
            <div>
              <div className="font-medium text-stone-800">Sync unitów</div>
              <div>
                {fmtWhen(freshQ.data?.units?.last_run_at)} · {freshQ.data?.units?.status || "?"}
                {freshQ.data?.units?.trigger ? ` · ${freshQ.data.units.trigger}` : ""}
              </div>
              {(freshQ.data?.units?.sync_created != null ||
                freshQ.data?.units?.sync_updated != null) && (
                <div>
                  +{freshQ.data?.units?.sync_created ?? 0} / ~{freshQ.data?.units?.sync_updated ?? 0}{" "}
                  · ingest {freshQ.data?.units?.ingest_processed ?? "–"} · content{" "}
                  {freshQ.data?.units?.content_processed ?? "–"}
                </div>
              )}
            </div>
            <div>
              <div className="font-medium text-stone-800">Whisper (ostatni run)</div>
              <div>{freshQ.data?.units?.whisper || freshQ.data?.background?.whisper_last || "brak danych"}</div>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => rematchM.mutate()} disabled={rematchM.isPending || !!bgJob}>
                {rematchM.isPending ? "Rematch…" : "Połącz po ASR"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => whisperM.mutate()} disabled={whisperM.isPending || !!bgJob}>
                {whisperM.isPending ? "Whisper…" : "Whisper backfill"}
              </Button>
              <Button size="sm" onClick={() => syncM.mutate()} disabled={syncM.isPending || !!bgJob}>
                {syncM.isPending ? "Sync…" : "Sync z dumpów"}
              </Button>
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-xs font-medium text-stone-800">
              Statystyka rolek ({stats?.reel_total ?? freshQ.data?.total_units ?? "–"})
            </div>
            {freshQ.isLoading && !stats ? (
              <p className="text-xs text-muted-foreground">Ładowanie statystyk…</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    { key: "asr", label: "z ASR", filter: "asr", tone: "emerald" as const },
                    { key: "caption_only", label: "tylko caption", filter: "", tone: "stone" as const },
                    { key: "pending", label: "pending", filter: "pending", tone: "stone" as const },
                    { key: "no_speech", label: "no_speech", filter: "no_speech", tone: "amber" as const },
                    { key: "no_audio", label: "no_audio", filter: "no_audio", tone: "amber" as const },
                    {
                      key: "media_unavailable",
                      label: "bez pliku",
                      filter: "media_unavailable",
                      tone: "rose" as const,
                    },
                    { key: "matched", label: "spięte IG+FB", filter: "", tone: "sky" as const },
                    { key: "ig_only", label: "tylko IG", filter: "", tone: "stone" as const },
                    { key: "fb_only", label: "tylko FB", filter: "", tone: "stone" as const },
                  ] as const
                ).map((row) => {
                  const n = Number((stats as Record<string, number> | undefined)?.[row.key] ?? 0);
                  const active = row.filter && contentStatus === row.filter;
                  return (
                    <button
                      key={row.key}
                      type="button"
                      title={row.filter ? "Filtruj listę" : undefined}
                      disabled={!row.filter}
                      onClick={() => row.filter && setStatusFilter(row.filter)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-left text-xs transition",
                        active
                          ? "border-brand bg-brand/10"
                          : "border-stone-200 bg-white hover:border-stone-300",
                        !row.filter && "cursor-default opacity-90",
                      )}
                    >
                      <Chip tone={row.tone}>{row.label}</Chip>
                      <span className="font-semibold tabular-nums text-stone-800">{n}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {stats && (stats.no_speech || 0) > 0 ? (
              <p className="mt-2 text-[11px] text-amber-800">
                {stats.no_speech} z etykietą no_speech (część to stary fałszywy stub). Whisper backfill
                może je ponowić.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => {
            setOffset(0);
            setQ(e.target.value);
          }}
          placeholder="Szukaj w opisie / ASR / URL…"
          className="h-9 min-w-[12rem] flex-1 rounded-md border border-input bg-background px-3 text-sm"
        />
        <Select
          value={order}
          onChange={(v) => setOrder(v)}
          className="h-9 w-40"
          aria-label="Sortowanie"
          options={[
            { value: "published_at", label: "Data" },
            { value: "score", label: "Score" },
            { value: "views", label: "Views" },
            { value: "comments", label: "Komentarze" },
            { value: "triggers", label: "Trigery" },
          ]}
        />
        <Select
          value={platform}
          onChange={(v) => {
            setOffset(0);
            setPlatform(v);
          }}
          className="h-9 w-32"
          aria-label="Platforma"
          options={[
            { value: "", label: "Platforma" },
            { value: "ig", label: "IG" },
            { value: "fb", label: "FB" },
            { value: "both", label: "IG+FB" },
          ]}
        />
        <Select
          value={matched}
          onChange={(v) => {
            setOffset(0);
            setMatched(v);
          }}
          className="h-9 w-36"
          aria-label="Match"
          options={[
            { value: "", label: "Match" },
            { value: "true", label: "Spięte" },
            { value: "false", label: "Niespięte" },
          ]}
        />
        <Select
          value={contentStatus}
          onChange={(v) => {
            setOffset(0);
            setContentStatus(v);
          }}
          className="h-9 w-44"
          aria-label="Status treści"
          options={[
            { value: "", label: "Status treści" },
            { value: "ready", label: "ready" },
            { value: "asr", label: "z ASR" },
            { value: "pending", label: "pending" },
            { value: "no_audio", label: "no_audio" },
            { value: "no_speech", label: "no_speech" },
            { value: "media_unavailable", label: "media_unavailable" },
          ]}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Lista rolek <span className="text-muted-foreground font-normal">({total})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {listQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Ładowanie…</p>
            ) : !items.length ? (
              <p className="text-sm text-muted-foreground">Brak rolek w filtrze. Uruchom Sync.</p>
            ) : (
              <ul className="max-h-[70vh] space-y-1 overflow-y-auto pr-1">
                {items.map((u) => {
                  const active = u.id === selectedId;
                  const snippet = (u.content_text || u.caption || "Brak treści").slice(0, 110);
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => select(u.id)}
                        className={cn(
                          "flex w-full gap-2 rounded border px-2 py-2 text-left transition-colors",
                          active
                            ? "border-stone-900 bg-stone-50"
                            : "border-stone-200 hover:border-stone-400",
                        )}
                      >
                        <Thumb url={u.thumb_url} format={u.format} />
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                            <span className="tabular-nums text-muted-foreground">{fmtDate(u.published_at)}</span>
                            <Chip>{platformOf(u)}</Chip>
                            <Chip tone={u.matched ? "emerald" : "stone"}>
                              {u.matched ? "spięte" : "osobno"}
                            </Chip>
                            <Chip tone={statusTone(u.content_status)}>{u.content_status || "–"}</Chip>
                            {u.life_class ? <Chip tone="sky">{u.life_class}</Chip> : null}
                          </div>
                          <p className="text-sm leading-snug">{snippet}</p>
                          <div className="flex flex-wrap gap-3 text-[11px] tabular-nums text-muted-foreground">
                            <span>views {fmtNum(u.metrics?.views)}</span>
                            <span>kom. {fmtNum(u.metrics?.comments)}</span>
                            <span>trig. {fmtNum(u.metrics?.triggers)}</span>
                            <span>score {u.score != null ? Number(u.score).toFixed(1) : "–"}</span>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex items-center justify-between pt-2 text-xs">
              <Button
                size="sm"
                variant="outline"
                disabled={offset <= 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
              >
                Poprzednie
              </Button>
              <span className="text-muted-foreground">
                {offset + 1}–{Math.min(offset + PAGE, total)} / {total}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={offset + PAGE >= total}
                onClick={() => setOffset(offset + PAGE)}
              >
                Następne
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Szczegół</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedId ? (
              <p className="text-sm text-muted-foreground">Wybierz rolkę z listy.</p>
            ) : detailQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Ładowanie szczegółu…</p>
            ) : !detail ? (
              <p className="text-sm text-muted-foreground">Nie znaleziono jednostki.</p>
            ) : (
              <>
                <div className="overflow-hidden rounded border border-stone-200 bg-black">
                  {detail.playback?.has_local && detail.playback.local_url ? (
                    <video
                      key={detail.playback.local_url}
                      src={detail.playback.local_url}
                      controls
                      playsInline
                      className="max-h-[420px] w-full"
                      poster={detail.playback.thumb_url || undefined}
                    />
                  ) : (
                    <div className="flex min-h-[180px] flex-col items-center justify-center gap-2 p-6 text-center text-sm text-stone-200">
                      <p>Brak lokalnego pliku wideo (Graph często nie oddaje media_url).</p>
                      <p className="text-xs text-stone-400">
                        ingest: {detail.playback?.ingest_status || detail.ingest_status || "–"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {detail.ig_url ? (
                    <a className="underline" href={detail.ig_url} target="_blank" rel="noreferrer">
                      Instagram
                    </a>
                  ) : null}
                  {detail.fb_url ? (
                    <a className="underline" href={detail.fb_url} target="_blank" rel="noreferrer">
                      Facebook
                    </a>
                  ) : null}
                  <Link
                    className="underline"
                    to={
                      commentsQ.data?.comments_open ||
                      `/komentarze?post=${detail.shortcode || detail.fb_id || ""}`
                    }
                  >
                    Otwórz w Komentarzach
                  </Link>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Chip tone="sky">{detail.tags?.life_class || detail.life_class || "life?"}</Chip>
                  <Chip>{detail.tags?.funnel_guess || detail.taxonomy?.funnel_guess || "funnel?"}</Chip>
                  <Chip tone={statusTone(detail.content_status)}>
                    {detail.tags?.content_status || detail.content_status}
                  </Chip>
                  <Chip tone={detail.matched ? "emerald" : "stone"}>
                    {detail.matched ? "spięte IG+FB" : "niespięte"}
                  </Chip>
                  {detail.content_source ? <Chip>{detail.content_source}</Chip> : null}
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[
                    ["Views", detail.metrics?.views],
                    ["Komentarze", detail.metrics?.comments],
                    ["Trigery", detail.metrics?.triggers],
                    ["Likes", detail.metrics?.likes],
                    ["Score", detail.score],
                    ["Data", fmtDate(detail.published_at)],
                  ].map(([label, val]) => (
                    <div key={String(label)} className="rounded border border-stone-200 px-2 py-1.5">
                      <div className="text-[11px] text-muted-foreground">{label}</div>
                      <div className="text-sm font-semibold tabular-nums">
                        {typeof val === "number" ? (label === "Score" ? val.toFixed(1) : fmtNum(val)) : val}
                      </div>
                    </div>
                  ))}
                </div>

                <section>
                  <h3 className="mb-1 text-sm font-medium">Opis (caption)</h3>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700">
                    {(detail.caption || "").trim() || "Brak podpisu w dumpie Graph."}
                  </p>
                </section>

                <section>
                  <h3 className="mb-1 text-sm font-medium">
                    {isCaptionFallback(detail.content_source, detail.content_status)
                      ? "Tekst (caption, bez ASR)"
                      : "Transkrypcja"}{" "}
                    <span className="font-normal text-muted-foreground">
                      ({detail.content_source || "brak źródła"})
                    </span>
                  </h3>
                  {isCaptionFallback(detail.content_source, detail.content_status) ? (
                    <p className="mb-1 text-xs text-amber-800">
                      To nie jest mowa z wideo. Uruchom Whisper backfill, żeby dociągnąć ASR.
                    </p>
                  ) : null}
                  <p className="max-h-64 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-stone-700">
                    {(detail.content_text || "").trim() || "Brak transkrypcji. Uruchom Whisper backfill."}
                  </p>
                </section>

                <section>
                  <h3 className="mb-1 text-sm font-medium">
                    Komentarze{" "}
                    <span className="font-normal text-muted-foreground">
                      ({commentsQ.data?.total ?? "…"})
                    </span>
                  </h3>
                  {commentsQ.isLoading ? (
                    <p className="text-sm text-muted-foreground">Ładowanie komentarzy…</p>
                  ) : !(commentsQ.data?.items || []).length ? (
                    <p className="text-sm text-muted-foreground">
                      Brak komentarzy pod shortcode/FB id (albo dump jeszcze nie złapał).
                    </p>
                  ) : (
                    <ul className="max-h-72 space-y-2 overflow-y-auto">
                      {(commentsQ.data?.items || []).map((c) => (
                        <li key={c.id} className="rounded border border-stone-100 px-2 py-1.5 text-sm">
                          <div className="text-[11px] text-muted-foreground">
                            {c.author || "?"} · {c.platform} · {(c.created_at || "").slice(0, 16)}
                          </div>
                          <p className="leading-snug">{c.text}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
